import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import FlatsViewTabs from '../../../../components/ui/FlatsViewTabs/FlatsViewTabs';
import { FlatsGridPreviewActions } from './components/FlatsGridPreviewActions/FlatsGridPreviewActions';
import { flatsViews } from '../../../../constants/flatsViews';
import { useFlatsGridPreviewNavigationBlocker } from '../../../../hooks/useFlatsGridPreviewNavigationBlocker';
import {
  resetFlatsGridPreviewData,
  setFlatsView,
  setSelectedFlats,
} from '../../../../store/actions/housingStockActions';
import EntranceTabs from './components/EntranceTabs/EntranceTabs';
import { ModalsProvider } from './providers/modals';
import Loader from './components/Loader/Loader';
import {
  selectFlatsData,
  selectFlatsView,
  selectHasGridData,
  selectHasGridPreview,
  selectUserInfo,
} from '../../lib/storeSelectors';
import ChessView from './components/ChessView/ChessView';
import CamerasView from './components/CamerasView/CamerasView';
import TableView from './components/TableView/TableView';
import styles from './Flats.module.scss';
import { MassActionsFlatsBtn } from '../../../../components/ui/MassActionsFlats';

const Header = ({ children, flatsView }) => {
  const hasGridPreview = useSelector(selectHasGridPreview);
  const { CanCreateFlatGrid } = useSelector(selectUserInfo);
  const selectedFlatsData = useSelector(selectFlatsData);

  const showTabs =
    flatsView === flatsViews.table ||
    (flatsView === flatsViews.grid && !hasGridPreview);

  const showPreviewActions = flatsView === flatsViews.grid && CanCreateFlatGrid;

  const showMassActions = selectedFlatsData && selectedFlatsData.length > 1;

  return (
    <div className={styles.navRow}>
      {showTabs && (
        <EntranceTabs
          view={flatsView}
          className={styles.tabs}
        />
      )}
      <div className={styles.navRowRightActionsWrapper}>
        {showMassActions && <MassActionsFlatsBtn flats={selectedFlatsData} />}
        {showPreviewActions && <FlatsGridPreviewActions />}
        {children}
      </div>
    </div>
  );
};

const GridPreviewNavigationBlocker = ({ children, flatsView }) => {
  const hasPreviewData = useSelector(selectHasGridPreview);
  const hasFlatsGrid = useSelector(selectHasGridData);

  const dispatch = useDispatch();

  useFlatsGridPreviewNavigationBlocker({
    isBlocking: hasPreviewData,
    hasFlatsGrid,
    onOk: ({ location, unblock, history }) => {
      dispatch(resetFlatsGridPreviewData());
      unblock();
      history.push(location);
    },
    onCancel: () => {
      if (flatsView !== flatsViews.grid) {
        dispatch(setFlatsView(flatsViews.grid));
      }
    },
    beforeUnloadCallback: () => {
      if (flatsView !== flatsViews.grid) {
        dispatch(setFlatsView(flatsViews.grid));
      }
    },
  });

  return children;
};

const viewComponents = {
  [flatsViews.table]: <TableView />,
  [flatsViews.grid]: <ChessView />,
  [flatsViews.camera]: <CamerasView className={styles.camerasView} />,
};

const Content = () => {
  const flatsView = useSelector(selectFlatsView);

  return (
    <GridPreviewNavigationBlocker flatsView={flatsView}>
      <Header flatsView={flatsView}>
        <FlatsViewTabs
          className={styles.flatsViewTabs}
          flatsView={flatsView}
        />
      </Header>

      {viewComponents[flatsView]}
    </GridPreviewNavigationBlocker>
  );
};

const Flats = () => {
  const dispatch = useDispatch();

  const location = useLocation();

  useEffect(() => {
    dispatch(setSelectedFlats([]));

    return () => {
      dispatch(setSelectedFlats([]));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const scrollToFlatId = params.get('scrollToFlatId');

    if (!scrollToFlatId) return;

    let attempts = 0;
    let cancelled = false;

    const tryScroll = () => {
      if (cancelled) return;
      attempts += 1;

      const el = document.querySelector(
        `[data-flat-id="${String(scrollToFlatId)}"]`
      );

      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'center' });
        return;
      }

      if (attempts < 15) {
        setTimeout(tryScroll, 100);
      }
    };

    const timeoutId = setTimeout(tryScroll, 0);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [location.search]);
  return (
    <ModalsProvider>
      <div className={styles.container}>
        <Loader>
          <Content />
        </Loader>
      </div>
    </ModalsProvider>
  );
};

export default Flats;
